import React, { useState, useEffect } from 'react';
import { clientAPI, proposalAPI, employeeAPI, ledgerAPI, adminAPI } from '../lib/api';
import { formatINR, formatDate, getStatusBadgeClass, shortId, todayISO, firstOfThisMonthISO, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import DatePicker from '../components/DatePicker';
import ClientPicker from '../components/ClientPicker';
import ReceivePaymentDialog from '../components/ReceivePaymentDialog';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Building2,
  Mail,
  Phone,
  FileText,
  Loader2,
  Eye,
  Users,
  Clock,
  DollarSign,
  TrendingUp,
  X,
  ScrollText,
  Download,
  GitMerge,
  CircleDollarSign
} from 'lucide-react';


const INDUSTRIES = [
  'Technology', 'Retail', 'FMCG', 'Real Estate', 'Healthcare', 'Education',
  'Finance', 'Entertainment', 'Manufacturing', 'F&B', 'Hospitality', 'Automotive',
  'Energy', 'Beauty', 'Fitness', 'Services', 'Conglomerate', 'Other'
];

const PAYMENT_TERMS = ['GST', 'Non-GST', 'Cash'];

const PAGE_SIZE = 24;

const EMPTY_FORM = () => ({
  name: '',
  industry: '',
  contact_person: '',
  email: '',
  phone: '',
  payment_terms: 'Non-GST',
  credit_days: '30',
  status: 'active',
  monthly_retainer: '',
  creatives_per_month: '',
  videos_per_month: '',
  shoots_per_month: '',
  total_hours_spent: '',
  payment_date: '',
  gstin: '',
  bill_to_address: '',
  bill_to_city: '',
  bill_to_state: '',
  followups_paused: false,
  team_members: []
});

export default function Clients() {
  const { can } = useAuth();
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(1);

  // Receive-payment dialog (one receipt across several invoices)
  const [recvClient, setRecvClient] = useState(null);

  // Statement-download dialog
  const [stmtClient, setStmtClient] = useState(null);
  const [stmtForm, setStmtForm] = useState({ date_start: firstOfThisMonthISO(), date_end: todayISO() });
  const [stmtBusy, setStmtBusy] = useState(false);

  const openStatement = (client) => {
    setStmtClient(client);
    setStmtForm({ date_start: firstOfThisMonthISO(), date_end: todayISO() });
  };

  const downloadStatement = async () => {
    if (!stmtClient) return;
    setStmtBusy(true);
    try {
      const res = await ledgerAPI.statementPdf(stmtClient.id, {
        date_start: stmtForm.date_start,
        date_end: stmtForm.date_end,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Statement_${(stmtClient.name || 'client').replace(/\s+/g, '_')}_${stmtForm.date_start}_${stmtForm.date_end}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Statement downloaded');
      setStmtClient(null);
    } catch (err) {
      // responseType 'blob' wraps the JSON error body; unwrap it so apiError sees `detail`.
      const body = err?.response?.data;
      if (body instanceof Blob) {
        try { err.response.data = JSON.parse(await body.text()); } catch { /* not JSON */ }
      }
      toast.error(apiError(err, 'Failed to generate statement'));
    } finally {
      setStmtBusy(false);
    }
  };

  // Merge-duplicate dialog: mergeFrom is removed, mergeInto is kept.
  const [mergeFrom, setMergeFrom] = useState(null);
  const [mergeInto, setMergeInto] = useState('');
  const [merging, setMerging] = useState(false);

  const openMerge = (client) => {
    setMergeFrom(client);
    setMergeInto('');
  };

  const doMerge = async () => {
    if (!mergeFrom || !mergeInto) return;
    setMerging(true);
    try {
      const res = await adminAPI.mergeClients(mergeInto, [mergeFrom.id]);
      toast.success(`${mergeFrom.name} merged into ${res.data?.kept?.name || 'the picked client'}`);
      setMergeFrom(null);
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Failed to merge clients'));
    } finally {
      setMerging(false);
    }
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  const [formData, setFormData] = useState(EMPTY_FORM());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, proposalsRes, employeesRes] = await Promise.all([
        clientAPI.getAll(),
        proposalAPI.getAll(),
        employeeAPI.getAll()
      ]);
      setClients(clientsRes.data);
      setProposals(proposalsRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM());
    setEditingClient(null);
    setActiveTab('info');
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      industry: client.industry || '',
      contact_person: client.contact_person || '',
      email: client.email || '',
      phone: client.phone || '',
      payment_terms: client.payment_terms || 'Non-GST',
      credit_days: String(client.credit_days ?? 30),
      status: client.status || 'active',
      monthly_retainer: client.monthly_retainer?.toString() || '',
      creatives_per_month: client.creatives_per_month?.toString() || '',
      videos_per_month: client.videos_per_month?.toString() || '',
      shoots_per_month: client.shoots_per_month?.toString() || '',
      total_hours_spent: client.total_hours_spent?.toString() || '',
      payment_date: client.payment_date || '',
      gstin: client.gstin || '',
      bill_to_address: client.bill_to_address || '',
      bill_to_city: client.bill_to_city || '',
      bill_to_state: client.bill_to_state || '',
      followups_paused: !!client.followups_paused,
      team_members: client.team_members || []
    });
    setDialogOpen(true);
  };

  const handleViewDetails = async (client) => {
    setSelectedClient(client);
    setDetailsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = {
        ...formData,
        credit_days: parseInt(formData.credit_days) || 0,
        monthly_retainer: parseFloat(formData.monthly_retainer) || 0,
        creatives_per_month: parseInt(formData.creatives_per_month) || 0,
        videos_per_month: parseInt(formData.videos_per_month) || 0,
        shoots_per_month: parseInt(formData.shoots_per_month) || 0,
        total_hours_spent: parseFloat(formData.total_hours_spent) || 0
      };

      if (editingClient) {
        await clientAPI.update(editingClient.id, data);
        toast.success('Client updated successfully');
      } else {
        await clientAPI.create(data);
        toast.success('Client added successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to save client'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      await clientAPI.delete(id);
      toast.success('Client deleted');
      fetchData();
    } catch (error) {
      toast.error(apiError(error, 'Failed to delete client'));
    }
  };

  const addTeamMember = () => {
    setFormData({
      ...formData,
      team_members: [...formData.team_members, { employee_id: '', employee_name: '', role: '', hours_allocated: 0 }]
    });
  };

  const updateTeamMember = (index, field, value) => {
    const newTeamMembers = [...formData.team_members];
    newTeamMembers[index] = { ...newTeamMembers[index], [field]: value };
    
    // If employee is selected, auto-fill name
    if (field === 'employee_id') {
      const employee = employees.find(e => e.id === value);
      if (employee) {
        newTeamMembers[index].employee_name = employee.name;
        newTeamMembers[index].role = employee.role;
      }
    }
    
    setFormData({ ...formData, team_members: newTeamMembers });
  };

  const removeTeamMember = (index) => {
    setFormData({
      ...formData,
      team_members: formData.team_members.filter((_, i) => i !== index)
    });
  };

  const getClientProposals = (clientId) => {
    return proposals.filter(p => p.client_id === clientId);
  };

  const getClientRevenue = (clientId) => {
    return proposals
      .filter(p => p.client_id === clientId && ['accepted', 'completed'].includes(p.status))
      .reduce((sum, p) => sum + (p.final_price || 0), 0);
  };

  const calculateTeamCost = (teamMembers) => {
    let cost = 0;
    teamMembers.forEach(member => {
      const employee = employees.find(e => e.id === member.employee_id);
      if (employee) {
        const hourlyRate = (employee.per_day_cost || 0) / 8;
        cost += hourlyRate * (member.hours_allocated || 0);
      }
    });
    return cost;
  };

  const filteredClients = clients.filter(client => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      client.name.toLowerCase().includes(term) ||
      (client.contact_person || '').toLowerCase().includes(term) ||
      (client.industry || '').toLowerCase().includes(term) ||
      (client.email || '').toLowerCase().includes(term);
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / PAGE_SIZE));
  // Deleting the last client on the last page would otherwise strand us on an empty page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const pagedClients = filteredClients.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const showingFrom = filteredClients.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, filteredClients.length);

  const totalMonthlyRetainer = clients.filter(c => c.status === 'active').reduce((sum, c) => sum + (c.monthly_retainer || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Clients</h1>
          <p className="text-gray-500 mt-1">Manage clients, team assignments, and profitability</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          {can('write') && (
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="bg-black hover:bg-black/90">
                <Plus className="w-4 h-4 mr-2" />
                Add Client
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full">
                  <TabsTrigger value="info" className="flex-1">Basic Info</TabsTrigger>
                  <TabsTrigger value="deliverables" className="flex-1">Deliverables</TabsTrigger>
                  <TabsTrigger value="team" className="flex-1">Team Members</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="clientName">Client Name *</Label>
                      <Input
                        id="clientName"
                        data-testid="client-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="industry">Industry</Label>
                      <Select 
                        value={formData.industry} 
                        onValueChange={(v) => setFormData({ ...formData, industry: v })}
                      >
                        <SelectTrigger data-testid="client-industry-select">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRIES.map(ind => (
                            <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactPerson">Contact Person</Label>
                      <Input
                        id="contactPerson"
                        value={formData.contact_person}
                        onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="text"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                      <p className="text-xs text-gray-500">Separate several addresses with commas</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                      <p className="text-xs text-gray-500">Used for WhatsApp invoices &amp; reminders (include country code, e.g. +91 98xxx xxxxx)</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="paymentTerms">Payment Terms *</Label>
                      <Select 
                        value={formData.payment_terms} 
                        onValueChange={(v) => setFormData({ ...formData, payment_terms: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_TERMS.map(term => (
                            <SelectItem key={term} value={term}>{term}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="creditDays">Credit days</Label>
                      <Input
                        id="creditDays"
                        type="number"
                        min="0"
                        placeholder="0 = due on receipt"
                        value={formData.credit_days}
                        onChange={(e) => setFormData({ ...formData, credit_days: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="paymentDate">Payment Day</Label>
                      <Input
                        id="paymentDate"
                        placeholder="e.g., 01, 15"
                        value={formData.payment_date}
                        onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="clientStatus">Status</Label>
                      <Select 
                        value={formData.status} 
                        onValueChange={(v) => setFormData({ ...formData, status: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="prospect">Prospect</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gstin">GSTIN</Label>
                      <Input
                        id="gstin"
                        value={formData.gstin}
                        onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
                        placeholder="27ABCDE1234F1Z5"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billAddress">Billing Address</Label>
                      <Input
                        id="billAddress"
                        value={formData.bill_to_address}
                        onChange={(e) => setFormData({ ...formData, bill_to_address: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billCity">City</Label>
                      <Input
                        id="billCity"
                        value={formData.bill_to_city}
                        onChange={(e) => setFormData({ ...formData, bill_to_city: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="billState">State</Label>
                      <Input
                        id="billState"
                        value={formData.bill_to_state}
                        onChange={(e) => setFormData({ ...formData, bill_to_state: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
                    <Label htmlFor="followupsPaused" className="text-sm font-medium">Pause automatic payment reminders</Label>
                    <Switch
                      id="followupsPaused"
                      checked={formData.followups_paused}
                      onCheckedChange={(v) => setFormData({ ...formData, followups_paused: v })}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="deliverables" className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="monthlyRetainer">Monthly Retainer (₹)</Label>
                      <Input
                        id="monthlyRetainer"
                        type="number"
                        value={formData.monthly_retainer}
                        onChange={(e) => setFormData({ ...formData, monthly_retainer: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="totalHours">Total Hours/Month</Label>
                      <Input
                        id="totalHours"
                        type="number"
                        value={formData.total_hours_spent}
                        onChange={(e) => setFormData({ ...formData, total_hours_spent: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="creatives">Creatives/Month</Label>
                      <Input
                        id="creatives"
                        type="number"
                        value={formData.creatives_per_month}
                        onChange={(e) => setFormData({ ...formData, creatives_per_month: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="videos">Videos/Month</Label>
                      <Input
                        id="videos"
                        type="number"
                        value={formData.videos_per_month}
                        onChange={(e) => setFormData({ ...formData, videos_per_month: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="shoots">Shoots/Month</Label>
                      <Input
                        id="shoots"
                        type="number"
                        value={formData.shoots_per_month}
                        onChange={(e) => setFormData({ ...formData, shoots_per_month: e.target.value })}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="team" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <Label>Assigned Team Members</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addTeamMember}>
                      <Plus className="w-4 h-4 mr-1" /> Add Member
                    </Button>
                  </div>
                  
                  <p className="text-xs text-gray-500 -mt-2">
                    Pick the people working on this client. Hours-per-month is optional and only used for true-COGS calculations.
                  </p>
                  {formData.team_members.length > 0 ? (
                    <div className="space-y-2">
                      {formData.team_members.map((member, index) => (
                        <div key={index} className="grid grid-cols-[1fr_96px_36px] sm:grid-cols-[1fr_140px_36px] items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                          <Select
                            value={member.employee_id}
                            onValueChange={(v) => updateTeamMember(index, 'employee_id', v)}
                          >
                            <SelectTrigger className="h-9 bg-white">
                              <SelectValue placeholder="Pick an employee…" />
                            </SelectTrigger>
                            <SelectContent>
                              {employees
                                .filter(e => e.status === 'active')
                                .filter(e => e.id === member.employee_id || !formData.team_members.some(m => m.employee_id === e.id))
                                .map(emp => (
                                  <SelectItem key={emp.id} value={emp.id}>
                                    {emp.name} <span className="text-gray-400">— {emp.role}</span>
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]*"
                            placeholder="Hours/mo (opt)"
                            value={member.hours_allocated || ''}
                            onChange={(e) => {
                              const v = e.target.value.replace(/[^0-9.]/g, '');
                              updateTeamMember(index, 'hours_allocated', v === '' ? 0 : parseFloat(v) || 0);
                            }}
                            className="h-9 bg-white text-right font-mono text-sm"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 w-9 p-0 text-red-500"
                            onClick={() => removeTeamMember(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      
                      {/* Show calculated team cost */}
                      {formData.team_members.length > 0 && (
                        <div className="p-4 bg-gray-100 rounded-lg">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Estimated Team Cost</span>
                            <span className="font-mono font-medium">{formatINR(calculateTeamCost(formData.team_members))}</span>
                          </div>
                          {formData.monthly_retainer && (
                            <div className="flex justify-between items-center mt-2">
                              <span className="text-sm text-gray-600">Profitability</span>
                              <span className={`font-mono font-medium ${
                                (parseFloat(formData.monthly_retainer) - calculateTeamCost(formData.team_members)) >= 0 
                                  ? 'text-green-700' : 'text-red-700'
                              }`}>
                                {formatINR(parseFloat(formData.monthly_retainer) - calculateTeamCost(formData.team_members))}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-400">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No team members assigned yet</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-black hover:bg-black/90">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingClient ? 'Update' : 'Add Client'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <Building2 className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{clients.filter(c => c.status === 'active').length}</p>
                <p className="text-sm text-gray-500">Active Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{formatINR(totalMonthlyRetainer)}</p>
                <p className="text-sm text-gray-500">Monthly Retainer</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <FileText className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">{proposals.length}</p>
                <p className="text-sm text-gray-500">Total Proposals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-mono">
                  {formatINR(proposals.filter(p => ['accepted', 'completed'].includes(p.status)).reduce((sum, p) => sum + (p.final_price || 0), 0))}
                </p>
                <p className="text-sm text-gray-500">Total Revenue</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="client-search"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="prospect">Prospect</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Clients Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="table-header text-left">Client</th>
                  <th className="table-header text-left hidden md:table-cell">Industry</th>
                  <th className="table-header text-right hidden md:table-cell">Monthly Retainer</th>
                  <th className="table-header text-center hidden md:table-cell">Deliverables</th>
                  <th className="table-header text-center hidden md:table-cell">Team</th>
                  <th className="table-header text-center hidden md:table-cell">Payment</th>
                  <th className="table-header text-center hidden md:table-cell">Status</th>
                  <th className="table-header text-center hidden md:table-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedClients.length > 0 ? (
                  pagedClients.map((client) => {
                    const teamCost = calculateTeamCost(client.team_members || []);
                    const profitability = (client.monthly_retainer || 0) - teamCost;
                    const actions = (
                      <>
                            {can('write') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRecvClient(client)}
                                title="Receive payment"
                                aria-label="Receive payment"
                                data-testid={`receive-payment-btn-${client.id}`}
                              >
                                <CircleDollarSign className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => openStatement(client)} title="Download statement of accounts">
                              <ScrollText className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleViewDetails(client)} title="View details">
                              <Eye className="w-4 h-4" />
                            </Button>
                            {can('write') && (
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(client)} title="Edit">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            {can('delete') && (
                              <Button variant="ghost" size="sm" onClick={() => openMerge(client)} title="Merge duplicate…">
                                <GitMerge className="w-4 h-4" />
                              </Button>
                            )}
                            {can('delete') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(client.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                      </>
                    );

                    return (
                      <tr key={client.id} className="table-row" data-testid={`client-row-${client.id}`}>
                        <td className="table-cell">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-black text-white flex items-center justify-center text-sm font-bold">
                              {client.name.charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium break-words">{client.name}</p>
                              {client.contact_person && (
                                <p className="text-xs text-gray-500">{client.contact_person}</p>
                              )}
                            </div>
                          </div>
                          <div className="md:hidden mt-2 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`badge ${getStatusBadgeClass(client.status)}`}>{client.status}</span>
                              {client.monthly_retainer ? <span className="text-xs font-mono text-gray-600">{formatINR(client.monthly_retainer)}/mo</span> : null}
                            </div>
                            <div className="flex items-center gap-0.5 flex-wrap -ml-2.5">{actions}</div>
                          </div>
                        </td>
                        <td className="table-cell hidden md:table-cell">
                          {client.industry && <span className="badge badge-gray">{client.industry}</span>}
                        </td>
                        <td className="table-cell text-right font-mono hidden md:table-cell">
                          {client.monthly_retainer ? formatINR(client.monthly_retainer) : '-'}
                        </td>
                        <td className="table-cell text-center text-xs hidden md:table-cell">
                          {(client.creatives_per_month || client.videos_per_month || client.shoots_per_month) ? (
                            <div className="space-y-0.5">
                              {client.creatives_per_month > 0 && <div>{client.creatives_per_month} Creatives</div>}
                              {client.videos_per_month > 0 && <div>{client.videos_per_month} Videos</div>}
                              {client.shoots_per_month > 0 && <div>{client.shoots_per_month} Shoots</div>}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="table-cell text-center hidden md:table-cell">
                          {client.team_members?.length > 0 ? (
                            <span className="badge badge-gray">{client.team_members.length} members</span>
                          ) : '-'}
                        </td>
                        <td className="table-cell text-center hidden md:table-cell">
                          <span className="badge badge-gray">{client.payment_terms}</span>
                          {client.payment_date && (
                            <div className="text-xs text-gray-500 mt-1">Day {client.payment_date}</div>
                          )}
                        </td>
                        <td className="table-cell text-center hidden md:table-cell">
                          <span className={`badge ${getStatusBadgeClass(client.status)}`}>
                            {client.status}
                          </span>
                        </td>
                        <td className="table-cell hidden md:table-cell">
                          <div className="flex items-center justify-center gap-1">
                            {actions}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-gray-400">
                      <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{searchTerm ? 'No clients match your search.' : 'No clients found'}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {filteredClients.length > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {showingFrom}–{showingTo} of {filteredClients.length}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Prev
            </Button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Client Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Client Details</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-black text-white flex items-center justify-center text-2xl font-bold">
                  {selectedClient.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-bold">{selectedClient.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    {selectedClient.industry && <span className="badge badge-gray">{selectedClient.industry}</span>}
                    <span className={`badge ${getStatusBadgeClass(selectedClient.status)}`}>{selectedClient.status}</span>
                  </div>
                </div>
              </div>

              {/* Profitability Summary */}
              {selectedClient.monthly_retainer > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-500">Monthly Retainer</p>
                    <p className="font-mono font-bold text-green-700">{formatINR(selectedClient.monthly_retainer)}</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-gray-500">Team Cost</p>
                    <p className="font-mono font-bold text-red-700">{formatINR(calculateTeamCost(selectedClient.team_members || []))}</p>
                  </div>
                  <div className={`p-4 rounded-lg ${
                    (selectedClient.monthly_retainer - calculateTeamCost(selectedClient.team_members || [])) >= 0 
                      ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    <p className="text-sm text-gray-500">Profitability</p>
                    <p className={`font-mono font-bold ${
                      (selectedClient.monthly_retainer - calculateTeamCost(selectedClient.team_members || [])) >= 0 
                        ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {formatINR(selectedClient.monthly_retainer - calculateTeamCost(selectedClient.team_members || []))}
                    </p>
                  </div>
                </div>
              )}

              {/* Deliverables */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Deliverables/Month</p>
                  <div className="mt-2 space-y-1">
                    <p className="text-sm"><span className="font-medium">{selectedClient.creatives_per_month || 0}</span> Creatives</p>
                    <p className="text-sm"><span className="font-medium">{selectedClient.videos_per_month || 0}</span> Videos</p>
                    <p className="text-sm"><span className="font-medium">{selectedClient.shoots_per_month || 0}</span> Shoots</p>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Contact</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <p>{selectedClient.contact_person || '-'}</p>
                    <p>{selectedClient.email || '-'}</p>
                    <p>{selectedClient.phone || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Team Members */}
              {selectedClient.team_members?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Assigned Team ({selectedClient.team_members.length})</h3>
                  <div className="space-y-2">
                    {selectedClient.team_members.map((member, idx) => {
                      const employee = employees.find(e => e.id === member.employee_id);
                      const hourlyRate = employee ? (employee.per_day_cost / 8) : 0;
                      const memberCost = hourlyRate * (member.hours_allocated || 0);
                      
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                              {member.employee_name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium">{member.employee_name || 'Unknown'}</p>
                              <p className="text-xs text-gray-500">{member.role || employee?.role}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono">{member.hours_allocated || 0} hrs/mo</p>
                            <p className="text-xs text-gray-500 font-mono">{formatINR(memberCost)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recent Proposals */}
              <div>
                <h3 className="font-semibold mb-3">Recent Proposals</h3>
                <div className="space-y-2">
                  {getClientProposals(selectedClient.id).slice(0, 5).map(proposal => (
                    <div key={proposal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{proposal.project_name}</p>
                        <p className="text-xs text-gray-500">{shortId(proposal.id)} • {formatDate(proposal.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono">{formatINR(proposal.final_price)}</p>
                        <span className={`badge ${getStatusBadgeClass(proposal.status)}`}>
                          {proposal.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {getClientProposals(selectedClient.id).length === 0 && (
                    <p className="text-center text-gray-400 py-4">No proposals yet</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receive payment (one receipt, several invoices) */}
      <ReceivePaymentDialog
        open={!!recvClient}
        onOpenChange={(o) => { if (!o) setRecvClient(null); }}
        client={recvClient}
        onSaved={fetchData}
      />

      {/* Customer Statement-of-Accounts dialog */}
      <Dialog open={!!stmtClient} onOpenChange={(o) => !o && setStmtClient(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Statement of Accounts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Client</p>
              <p className="font-medium">{stmtClient?.name}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="stmt-start">From</Label>
                <DatePicker
                  id="stmt-start"
                  value={stmtForm.date_start}
                  onChange={(v) => setStmtForm({ ...stmtForm, date_start: v })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="stmt-end">To</Label>
                <DatePicker
                  id="stmt-end"
                  value={stmtForm.date_end}
                  onChange={(v) => setStmtForm({ ...stmtForm, date_end: v })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setStmtClient(null)} disabled={stmtBusy}>
                Cancel
              </Button>
              <Button type="button" onClick={downloadStatement} disabled={stmtBusy} className="bg-black hover:bg-black/90 text-white">
                {stmtBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Download PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Merge duplicate client dialog (admin) */}
      <Dialog open={!!mergeFrom} onOpenChange={(o) => !o && setMergeFrom(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Merge duplicate client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Merge {mergeFrom?.name} into:</Label>
              <ClientPicker
                clients={clients.filter((c) => c.id !== mergeFrom?.id)}
                value={mergeInto}
                onChange={setMergeInto}
              />
            </div>
            <p className="text-xs text-gray-500">
              Invoices, proposals and payments move to the client you pick; blank details are filled from this one; this client is removed (a backup copy is kept).
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setMergeFrom(null)} disabled={merging}>
                Cancel
              </Button>
              <Button type="button" onClick={doMerge} disabled={merging || !mergeInto} className="bg-black hover:bg-black/90 text-white">
                {merging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <GitMerge className="w-4 h-4 mr-2" />}
                Merge
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
